import React from "react";
import styled from "styled-components";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";

export enum CompanyOnboardingStep
{
    Representatives = 1,
    Subscription = 2,
    CompanyData = 3,
    CompanyKyc = 4,
    PowersReview = 5,
    Active = 6,
}

interface CompanyOnboardingStepDefinition
{
    step: CompanyOnboardingStep;
    title: string;
    description: string;
}

// Pasos del alta de empresa en el orden en que se muestran
export const CompanyOnboardingSteps: CompanyOnboardingStepDefinition[] =
[
    { step: CompanyOnboardingStep.Representatives, title: "Representantes", description: "Alta con DNI" },
    { step: CompanyOnboardingStep.Subscription, title: "Suscripción", description: "345 € el primer año · 114,99 € años siguientes" },
    { step: CompanyOnboardingStep.CompanyData, title: "Alta de empresa", description: "Datos, documentación y representantes" },
    { step: CompanyOnboardingStep.CompanyKyc, title: "KYC empresa", description: "Conocer al cliente" },
    { step: CompanyOnboardingStep.PowersReview, title: "Bastanteo", description: "Revisión de poderes por Diaphanum" },
    { step: CompanyOnboardingStep.Active, title: "Activa", description: "Empresa operativa" },
];

type StepState = "done" | "current" | "pending";

const getStepState = (step: CompanyOnboardingStep, currentStep: CompanyOnboardingStep): StepState =>
{
    if(step < currentStep)
    {
        return "done";
    }

    return step === currentStep ? "current" : "pending";
};

interface CompanyOnboardingStepperProps
{
    currentStep: CompanyOnboardingStep;
}

const CompanyOnboardingStepper: React.FC<CompanyOnboardingStepperProps> = ({ currentStep }) =>
{
    return(
        <StepperList aria-label={CompanyOnboardingStrings.stepperAriaLabel}>
            {CompanyOnboardingSteps.map((definition) =>
            {
                const state = getStepState(definition.step, currentStep);

                return(
                    <StepItem key={definition.step} className={state} aria-current={state === "current" ? "step" : undefined}>
                        <StepDot>{definition.step}</StepDot>
                        <StepTitle>{definition.title}</StepTitle>
                        <StepDescription>{definition.description}</StepDescription>
                    </StepItem>
                );
            })}
        </StepperList>
    );
};

export default CompanyOnboardingStepper;

const StepperList = styled.ol`
    list-style: none;
    margin: 0;
    padding: 24px 16px;
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    background: ${(props) => props.theme?.palette?.tertiary || "#F3F6F8"};
    border-radius: 20px;
    cursor: default;

    /* En pantallas estrechas el stepper se desplaza en horizontal para no aplastar los textos */
    @media screen and (max-width: 899px)
    {
        grid-template-columns: repeat(6, 140px);
        overflow-x: auto;
    }
`;

const StepItem = styled.li`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 6px;
    position: relative;
    padding: 0 6px;

    /* Conector hacia el siguiente paso */
    &:not(:last-child)::after
    {
        content: "";
        position: absolute;
        top: 18px;
        left: calc(50% + 26px);
        right: calc(-50% + 26px);
        height: 2px;
        background: #D6DEE2;
    }

    &.done:not(:last-child)::after
    {
        background: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
    }

    &.done > span:first-child
    {
        background: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
        border-color: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
        color: #ffffff;
    }

    &.current > span:first-child
    {
        border-color: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
        color: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
        box-shadow: 0 0 0 5px #FBE9EE;
    }

    &.current > b
    {
        color: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
    }
`;

const StepDot = styled.span`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 15px;
    background: #ffffff;
    border: 2px solid #D6DEE2;
    color: #5f7172;
    position: relative;
    z-index: 1;
`;

const StepTitle = styled.b`
    font-size: 15px;
    font-weight: 600;
    color: #2a4143;
`;

const StepDescription = styled.small`
    font-size: 13px;
    color: #5f7172;
    line-height: 1.3;
`;
